import { useEffect, useState } from "preact/hooks";
import { appEngine, clearSignInProblem, defaultDeviceName, describeSyncFix, describeSyncStatus, signInProblem, startSignIn, subscribeSignInProblem, type SignInProblem } from "../sync";
import { useSyncSession, useSyncStatus } from "./sync-status";

/**
 * The two Sync cards (issue #26): the one a signed-out Device shows, which names this Device and
 * starts the Worker's Google flow, and the one the About section shows once signed in, which says
 * what Sync is doing and what to do about it.
 *
 * Signing in is optional: until it happens this card is the only thing on the home screen that
 * Sync adds, and nothing else in the app changes.
 */

function useSignInProblem(): SignInProblem | null {
  const [problem, setProblem] = useState<SignInProblem | null>(() => signInProblem());
  useEffect(() => subscribeSignInProblem(setProblem), []);
  return problem;
}

/** The sign-in card, and the first sign-in's upload while it runs. */
export function SyncSignIn() {
  const session = useSyncSession();
  const status = useSyncStatus();
  const problem = useSignInProblem();
  const [device, setDevice] = useState(() => defaultDeviceName());
  const [going, setGoing] = useState(false);
  const [failed, setFailed] = useState<string>();

  if (session === undefined) return null;

  // The first sign-in uploads everything this Device already holds; it happens where the card was.
  if (session) {
    if (session.syncedAt !== null) return null;
    return (
      <section class="sync-signin">
        <h2>Sync</h2>
        <div class="card">
          <p role="status">
            <strong>Signed in as {session.account.email ?? session.account.name ?? "this Account"}</strong>
          </p>
          <p class="muted">This device is called “{session.device.name}”. {describeSyncStatus(status)}</p>
          {status.phase === "syncing" && <progress aria-label="First sync" />}
          {status.phase !== "syncing" && <p class="muted">{describeSyncFix(status) ?? "Everything on this device is on its way to the Account."}</p>}
        </div>
      </section>
    );
  }

  const go = () => {
    setFailed(undefined);
    setGoing(true);
    startSignIn(device.trim() || defaultDeviceName()).catch((e: unknown) => {
      setGoing(false);
      setFailed(`Sign-in could not start: ${(e as Error).message}`);
    });
  };

  return (
    <section class="sync-signin">
      <h2>Sync</h2>
      <div class="card">
        <p><strong>Sign in to sync your Characters across devices.</strong></p>
        <p class="muted">
          Your Characters, Bookmarks and Recents are kept on an Account and are the same on every device you sign in on.
          The app works in full without one; nothing leaves this device until you sign in.
        </p>
        <p class="muted">Deleting a Character here is immediate. Signed in, a deletion can be undone for 30 days, on any device.</p>
        {problem && (
          <p class="notice" role="alert">
            {problem.message} <button type="button" class="linkish" onClick={clearSignInProblem}>dismiss</button>
          </p>
        )}
        {failed && <p class="notice" role="alert">{failed}</p>}
        <label class="field">
          <span class="muted">This device’s name</span>
          <input type="text" value={device} aria-label="This device’s name" placeholder={defaultDeviceName()} onInput={(e) => setDevice((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <span class="actions">
          <button type="button" class="primary" disabled={going} onClick={go}>Sign in with Google</button>
        </span>
      </div>
    </section>
  );
}

/** The About section's Sync card: the Account, this Device, what Sync is doing, and the way on. */
export function SyncCard() {
  const session = useSyncSession();
  const status = useSyncStatus();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();

  if (!session) return null;
  const fix = describeSyncFix(status);

  const syncNow = () => {
    setBusy(true);
    setMessage(undefined);
    appEngine()
      .syncNow({ force: true })
      .then(
        (outcome) => setMessage(outcome.stopped ? `Sync stopped: ${outcome.stopped}` : `Sent ${outcome.pushed}, received ${outcome.pulled}.`),
        (e: unknown) => setMessage(String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <>
      <h3 id="sync">Sync</h3>
      <div class="card sync-card">
        <p role="status"><strong>{describeSyncStatus(status)}</strong></p>
        {fix && <p class="muted">{fix}</p>}
        <p class="muted">
          {session.account.email ?? session.account.name ?? "Signed in"} · this device is “{session.device.name}”
        </p>
        {message && <p class="notice" role="status">{message}</p>}
        <span class="actions">
          <button type="button" disabled={busy} onClick={syncNow}>Sync now</button>
          <a href="#/account" class="button">Account…</a>
        </span>
      </div>
    </>
  );
}
